import { EWorksheetStatus } from '@if/core/types'
import * as yup from '@if/core/yup'
import {
  BaseWorksheetFormConfig,
  BaseWorksheetFormValues,
  buildBaseWorksheetFormInitialValues,
  StoredTermAndPlan,
  useBaseWorksheetFormFields,
  useBaseWorksheetFormReady,
} from '@src/components/WorksheetForm'
import { BaseWorksheetDto } from '@src/data/types/BaseWorksheetSchema'
import { EasyCareWorksheet, EditEasyCareWorksheetDto } from '@src/data/types/EasyCareWorksheetSchema'
import { useMemo } from 'react'

export type EasyCareEditValues = BaseWorksheetFormValues

export type EasyCareEditSchema = ReturnType<typeof useEasyCareEditSchema>

export type EasyCareEditSubmitValues = yup.InferSubmit<EasyCareEditSchema>

export const useEasyCareEditSchema = (config: BaseWorksheetFormConfig) => {
  const fields = useBaseWorksheetFormFields(config)

  return useMemo(
    () =>
      yup.object({
        amountRequested: fields.amountRequested,
        paymentPlanId: fields.paymentPlanId,
        term: fields.term,
        paymentFrequency: fields.paymentFrequency,
        activationDate: fields.activationDate,
        firstPaymentDate: fields.firstPaymentDate,
        includeProtection: fields.includeProtection,
        isHighEstimate: fields.isHighEstimate,
      }),
    [fields],
  )
}

export const fromWorksheet = (defaults: EasyCareEditValues, worksheet: EasyCareWorksheet): EasyCareEditValues =>
  buildBaseWorksheetFormInitialValues(defaults, worksheet as BaseWorksheetDto, null)

export const useEasyCareEditForm = (config: BaseWorksheetFormConfig, worksheet: EasyCareWorksheet) => {
  const schema = useEasyCareEditSchema(config)
  const isReady = useBaseWorksheetFormReady(config)

  const defaultValues = useMemo<EasyCareEditValues | undefined>(() => {
    if (!isReady) return undefined
    return fromWorksheet(schema.getDefault() as EasyCareEditValues, worksheet)
  }, [isReady, schema, worksheet])

  return { schema, defaultValues, isReady }
}

export const getStoredTermAndPlan = (worksheet: EasyCareWorksheet): StoredTermAndPlan | null =>
  worksheet.paymentPlanId && worksheet.term ? { paymentPlanId: worksheet.paymentPlanId, term: worksheet.term } : null

export const toEditEasyCareWorksheetDto = (
  values: EasyCareEditSubmitValues,
  worksheet: EasyCareWorksheet,
): EditEasyCareWorksheetDto => ({
  creditApplicationId: worksheet.creditApplicationId,
  versionTag: worksheet.versionTag,
  status: EWorksheetStatus.Active,
  amountRequested: values.amountRequested,
  term: values.term,
  paymentFrequency: values.paymentFrequency,
  deliveryOn: values.activationDate.toString(),
  firstPaymentOn: values.firstPaymentDate.toString(),
})
